<template>

    <SeoHead :seo="seo" />

    <main-layout>
        <SeoBreadcrumbs :seo="seo" />
        <PageHeader title="Матч" />
        <div
            class="match-header"
            :class="{ 'match-header--with-video': hasMatchVideos }"
        >
            <div class="match-header__score">
                <div class="teams-score-row">
                    <component
                        :is="teamPublicPath(game.home_team) ? 'a' : 'div'"
                        :href="teamPublicPath(game.home_team) || undefined"
                        class="team home-team"
                        :class="{ 'team--link': !!teamPublicPath(game.home_team) }"
                    >
                        <div class="team-logo" v-if="game.home_team?.logo_path">
                            <img
                                :src="game.home_team.logo_path"
                                :alt="game.home_team.name"
                                width="64"
                                height="64"
                                loading="eager"
                            />
                        </div>
                        <div class="team-logo placeholder" v-else>
                            {{ game.home_team?.name?.charAt(0) }}
                        </div>
                        <div class="team-name">{{ game.home_team?.name }}</div>
                    </component>

                    <div class="match-score">
                        <div class="score-main">{{ game.home_score }}:{{ game.away_score }}</div>
                        <div v-if="game.sets_score && game.sets_score.length > 0" class="sets-score-centered">
                            <div class="sets-label">Счет в партиях:</div>
                            <div class="sets-results">{{ formatSetsScore(game.sets_score) }}</div>
                        </div>
                    </div>

                    <component
                        :is="teamPublicPath(game.away_team) ? 'a' : 'div'"
                        :href="teamPublicPath(game.away_team) || undefined"
                        class="team away-team"
                        :class="{ 'team--link': !!teamPublicPath(game.away_team) }"
                    >
                        <div class="team-logo" v-if="game.away_team?.logo_path">
                            <img
                                :src="game.away_team.logo_path"
                                :alt="game.away_team.name"
                                width="64"
                                height="64"
                                loading="eager"
                            />
                        </div>
                        <div class="team-logo placeholder" v-else>
                            {{ game.away_team?.name?.charAt(0) }}
                        </div>
                        <div class="team-name">{{ game.away_team?.name }}</div>
                    </component>
                </div>
            </div>

            <div class="match-header__info">
                <div class="match-info">
                    <div class="competition-title">{{ getCompetitionName(game.competition_type) }}</div>
                    <div class="stage-info">{{ getStageName(game.stage) }}</div>
                    <div class="match-date">{{ game.match_date }}</div>
                    <div v-if="game.match_time" class="match-time">{{ formatMatchTime(game.match_time) }}</div>
                    <div v-if="game.venue" class="match-venue">{{ game.venue }}</div>
                    <div v-if="game.main_referee || game.second_referee" class="match-referees">
                        <span v-if="game.main_referee">1-й судья: {{ game.main_referee.full_name }}</span>
                        <span v-if="game.second_referee">2-й судья: {{ game.second_referee.full_name }}</span>
                    </div>
                </div>
            </div>

            <div v-if="hasMatchVideos" class="match-header__videos">
                <div v-if="game.match_video_embed_url" class="match-video">
                    <iframe
                        :src="game.match_video_embed_url"
                        title="Видео матча"
                        loading="lazy"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                        referrerpolicy="strict-origin-when-cross-origin"
                    />
                </div>
                <div v-if="game.match_analysis_embed_url" class="match-video">
                    <iframe
                        :src="game.match_analysis_embed_url"
                        title="Разбор матча"
                        loading="lazy"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                        referrerpolicy="strict-origin-when-cross-origin"
                    />
                </div>
            </div>
        </div>

        <PointFlowChart
            v-if="hasPointFlow"
            :point-flow="pointFlow"
            :home-color="homeFlowColor"
            :away-color="awayFlowColor"
        />

        <!-- Панель управления -->
        <div class="controls">
            <div class="set-tabs">
                <button @click="selectedSet = 'all'" :class="{ active: selectedSet === 'all' }" class="set-tab-btn">
                    Матч
                </button>
                <button v-for="setNum in setsRange" :key="setNum" @click="selectedSet = setNum"
                    :class="{ active: selectedSet == setNum }" class="set-tab-btn">
                    Сет {{ setNum }}
                </button>
            </div>
        </div>

        <!-- Проверка наличия данных статистики -->
        <div v-if="!hasStatsData" class="no-stats-message">
            <div class="no-stats-icon">📊</div>
            <h3>Статистика матча недоступна</h3>
            <p>Данные статистики для этого матча еще не загружены или обработаны</p>
        </div>

        <!-- Основная таблица статистики -->
        <div v-else class="statistics-content">
            <div class="stats-table">
                <!-- Заголовки команд -->
                <div class="teams-headers">
                    <component
                        :is="teamPublicPath(game.home_team) ? 'a' : 'div'"
                        :href="teamPublicPath(game.home_team) || undefined"
                        class="team-header-cell"
                    >
                        <div class="team-logo-header" v-if="game.home_team?.logo_path">
                            <img :src="game.home_team.logo_path" :alt="game.home_team.name" />
                        </div>
                        <div class="team-logo-header placeholder" v-else>
                            {{ game.home_team?.name?.charAt(0) }}
                        </div>
                    </component>
                    <component
                        :is="teamPublicPath(game.away_team) ? 'a' : 'div'"
                        :href="teamPublicPath(game.away_team) || undefined"
                        class="team-header-cell"
                    >
                        <div class="team-logo-header" v-if="game.away_team?.logo_path">
                            <img :src="game.away_team.logo_path" :alt="game.away_team.name" />
                        </div>
                        <div class="team-logo-header placeholder" v-else>
                            {{ game.away_team?.name?.charAt(0) }}
                        </div>
                    </component>
                </div>

                <!-- Строки элементов -->
                <div class="stats-rows">
                    <!-- Атака -->
                    <div class="stat-section">
                        <div class="stat-header" @click="toggleExpanded('attack')">
                            <span class="expand-icon" :class="{ expanded: expandedSections.attack }">▶</span>
                            <span class="stat-title">Атака</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('attack', 'home', 'total')"
                                            :class="getSortClass('attack', 'home', 'total')"
                                            title="Общее количество атак">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.home_team.stats.attack.total }}
                                        </div>
                                        <div class="stat-value efficiency" title="Эффективность атаки">({{
                                            currentStats.home_team.stats.attack.efficiency || 0 }}%)</div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'home', 'perfect')"
                                            :class="getSortClass('attack', 'home', 'perfect')" title="Очко">
                                            #</div>
                                        <div class="stat-value perfect">{{
                                            currentStats.home_team.stats.attack.perfect || '0' }}
                                        </div>
                                        <div class="stat-value percent"
                                            title="Процент реализованных атак от общего количества"
                                            v-if="currentStats.home_team.stats.attack.total > 0">
                                            ({{ formatPercent(((currentStats.home_team.stats.attack.perfect /
                                                currentStats.home_team.stats.attack.total) * 100)) }})
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'home', 'positive')"
                                            :class="getSortClass('attack', 'home', 'positive')" title="Усложнено">
                                            +</div>
                                        <div class="stat-value neutral">{{
                                            currentStats.home_team.stats.attack.positive ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'home', 'neutral')"
                                            :class="getSortClass('attack', 'home', 'neutral')" title="Не забито">!</div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.attack.neutral
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'home', 'negative')"
                                            :class="getSortClass('attack', 'home', 'negative')" title="Плохо">-
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.attack.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('attack', 'home', 'slash')"
                                            :class="getSortClass('attack', 'home', 'slash')" title="Блок">/</div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.attack.slash
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('attack', 'home', 'error')"
                                            :class="getSortClass('attack', 'home', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.attack.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('attack', 'away', 'total')"
                                            :class="getSortClass('attack', 'away', 'total')"
                                            title="Общее количество атак">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.away_team.stats.attack.total }}
                                        </div>
                                        <div class="stat-value efficiency" title="Эффективность атаки">({{
                                            currentStats.away_team.stats.attack.efficiency || 0 }}%)</div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'away', 'perfect')"
                                            :class="getSortClass('attack', 'away', 'perfect')" title="Очко">
                                            #</div>
                                        <div class="stat-value perfect">{{
                                            currentStats.away_team.stats.attack.perfect || '0' }}
                                        </div>
                                        <div class="stat-value percent"
                                            title="Процент реализованных атак от общего количества"
                                            v-if="currentStats.away_team.stats.attack.total > 0">
                                            ({{ formatPercent(((currentStats.away_team.stats.attack.perfect /
                                                currentStats.away_team.stats.attack.total) * 100)) }})
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'away', 'positive')"
                                            :class="getSortClass('attack', 'away', 'positive')" title="Усложнено">+
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.attack.positive
                                            || '0' }}</div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'away', 'neutral')"
                                            :class="getSortClass('attack', 'away', 'neutral')" title="Не забито">!</div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.attack.neutral
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('attack', 'away', 'negative')"
                                            :class="getSortClass('attack', 'away', 'negative')" title="Плохо">-</div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.attack.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('attack', 'away', 'slash')"
                                            :class="getSortClass('attack', 'away', 'slash')" title="Блок">/</div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.attack.slash
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('attack', 'away', 'error')"
                                            :class="getSortClass('attack', 'away', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.attack.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция атаки -->
                    <div v-if="expandedSections.attack" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('attack', 'home')"
                                    :key="`attack-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <div class="stat-col-group">
                                            <span class="stat-col total">{{ player.stats.total }}</span>
                                            <span class="stat-col efficiency" title="Эффективность атаки">({{
                                                player.stats.efficiency !== undefined ? player.stats.efficiency + '%' :
                                                    '0%' }})</span>
                                        </div>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                            <span class="stat-col percent" v-if="player.stats.total > 0">({{
                                                formatPercent(player.stats.points_percent) }})</span>
                                        </div>
                                        <span class="stat-col neutral">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('attack', 'away')"
                                    :key="`attack-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <div class="stat-col-group">
                                            <span class="stat-col total">{{ player.stats.total }}</span>
                                            <span class="stat-col efficiency" title="Эффективность атаки">{{
                                                player.stats.efficiency !== undefined ? player.stats.efficiency + '%' :
                                                    '0%' }}</span>
                                        </div>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                            <span class="stat-col percent" v-if="player.stats.total > 0">({{
                                                formatPercent(player.stats.points_percent) }})</span>
                                        </div>
                                        <span class="stat-col neutral">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ? player.stats.error :
                                            '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Подача -->
                    <div class="stat-section">
                        <div class="stat-header" @click="toggleExpanded('serve')">
                            <span class="expand-icon" :class="{ expanded: expandedSections.serve }">▶</span>
                            <span class="stat-title">Подача</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('serve', 'home', 'total')"
                                            :class="getSortClass('serve', 'home', 'total')"
                                            title="Общее количество подач">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.home_team.stats.serve.total }}
                                        </div>
                                        <div class="stat-value efficiency" title="Эффективность подачи">({{
                                            formatPercent(currentStats.home_team.stats.serve.efficiency) }})</div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'home', 'perfect')"
                                            :class="getSortClass('serve', 'home', 'perfect')" title="Эйс">
                                            #</div>
                                        <div class="stat-value perfect">{{ currentStats.home_team.stats.serve.perfect ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('serve', 'home', 'slash')"
                                            :class="getSortClass('serve', 'home', 'slash')"
                                            title="Мяч после приема вернулся на свою сторону">/
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.serve.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'home', 'positive')"
                                            :class="getSortClass('serve', 'home', 'positive')"
                                            title="Прием подачи на 'минус'">+
                                        </div>
                                        <div class=" stat-value neutral">{{ currentStats.home_team.stats.serve.positive
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'home', 'neutral')"
                                            :class="getSortClass('serve', 'home', 'neutral')"
                                            title="Прием подачи на 'знак'">!</div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.serve.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'home', 'negative')"
                                            :class="getSortClass('serve', 'home', 'negative')"
                                            title="Прием подачи на 'плюс'">-
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.serve.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('serve', 'home', 'error')"
                                            :class="getSortClass('serve', 'home', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.serve.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('serve', 'away', 'total')"
                                            :class="getSortClass('serve', 'away', 'total')"
                                            title="Общее количество подач">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.away_team.stats.serve.total }}
                                        </div>
                                        <div class="stat-value efficiency" title="Эффективность подачи">({{
                                            formatPercent(currentStats.away_team.stats.serve.efficiency) }})</div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'away', 'perfect')"
                                            :class="getSortClass('serve', 'away', 'perfect')" title="Эйс">
                                            #</div>
                                        <div class="stat-value perfect">{{ currentStats.away_team.stats.serve.perfect ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('serve', 'away', 'slash')"
                                            :class="getSortClass('serve', 'away', 'slash')"
                                            title="Мяч после приема вернулся на свою сторону">/
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.serve.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'away', 'positive')"
                                            :class="getSortClass('serve', 'away', 'positive')"
                                            title="Прием подачи на 'минус'">+
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.serve.positive
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'away', 'neutral')"
                                            :class="getSortClass('serve', 'away', 'neutral')"
                                            title="Прием подачи на 'знак'">!</div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.serve.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('serve', 'away', 'negative')"
                                            :class="getSortClass('serve', 'away', 'negative')"
                                            title="Прием подачи на 'плюс'">-
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.serve.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('serve', 'away', 'error')"
                                            :class="getSortClass('serve', 'away', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.serve.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция подачи -->
                    <div v-if="expandedSections.serve" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('serve', 'home')"
                                    :key="`serve-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <div class="stat-col-group">
                                            <span class="stat-col total">{{ player.stats.total }}</span>
                                            <span class="stat-col efficiency" title="Эффективность подачи">({{
                                                formatPercent(player.stats.efficiency) }})</span>
                                        </div>
                                        <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('serve', 'away')"
                                    :key="`serve-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <div class="stat-col-group">
                                            <span class="stat-col total">{{ player.stats.total }}</span>
                                            <span class="stat-col efficiency" title="Эффективность подачи">({{
                                                formatPercent(player.stats.efficiency) }})</span>
                                        </div>
                                        <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Блок -->
                    <div class="stat-section">
                        <div class="stat-header" @click="toggleExpanded('block')">
                            <span class="expand-icon" :class="{ expanded: expandedSections.block }">▶</span>
                            <span class="stat-title">Блок</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('block', 'home', 'total')"
                                            :class="getSortClass('block', 'home', 'total')" title="Общее количество">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.home_team.stats.block.total || '0'
                                        }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'home', 'perfect')"
                                            :class="getSortClass('block', 'home', 'perfect')" title="Блок">
                                            #</div>
                                        <div class="stat-value perfect">{{ currentStats.home_team.stats.block.perfect ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'home', 'positive')"
                                            :class="getSortClass('block', 'home', 'positive')"
                                            title="Хорошее смягчение">+
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.block.positive
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'home', 'neutral')"
                                            :class="getSortClass('block', 'home', 'neutral')" title="Смягчение">!</div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.block.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('block', 'home', 'slash')"
                                            :class="getSortClass('block', 'home', 'slash')" title="Смягчение">/
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.block.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'home', 'negative')"
                                            :class="getSortClass('block', 'home', 'negative')" title="Плохое смягчение">
                                            -
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.block.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('block', 'home', 'error')"
                                            :class="getSortClass('block', 'home', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.block.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('block', 'away', 'total')"
                                            :class="getSortClass('block', 'away', 'total')" title="Общее количество">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.away_team.stats.block.total || '0'
                                        }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'away', 'perfect')"
                                            :class="getSortClass('block', 'away', 'perfect')" title="Блок">
                                            #</div>
                                        <div class="stat-value perfect">{{ currentStats.away_team.stats.block.perfect ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'away', 'positive')"
                                            :class="getSortClass('block', 'away', 'positive')"
                                            title="Хорошее смягчение">+
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.block.positive
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'away', 'neutral')"
                                            :class="getSortClass('block', 'away', 'neutral')" title="Смягчение">!</div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.block.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('block', 'away', 'slash')"
                                            :class="getSortClass('block', 'away', 'slash')" title="Смягчение">/
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.block.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('block', 'away', 'negative')"
                                            :class="getSortClass('block', 'away', 'negative')" title="Плохое смягчение">
                                            -
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.block.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('block', 'away', 'error')"
                                            :class="getSortClass('block', 'away', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.block.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция блока -->
                    <div v-if="expandedSections.block" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('block', 'home')"
                                    :key="`block-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total || '0' }}</span>
                                        <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('block', 'away')"
                                    :key="`block-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total || '0' }}</span>
                                        <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Прием -->
                    <div class="stat-section">
                        <div class="stat-header" @click="toggleExpanded('receive')">
                            <span class="expand-icon" :class="{ expanded: expandedSections.receive }">▶</span>
                            <span class="stat-title">Прием</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'home', 'total')"
                                            :class="getSortClass('receive', 'home', 'total')"
                                            title="Общее количество приемов">Общее</div>
                                        <div class="stat-value total">{{ currentStats.home_team.stats.receive.total }}
                                        </div>
                                        <div class="stat-value efficiency" title="Эффективность приема">({{
                                            formatPercent(currentStats.home_team.stats.receive.efficiency) }})</div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'home', 'perfect')"
                                            :class="getSortClass('receive', 'home', 'perfect')" title="Идеальный прием">
                                            #</div>
                                        <div class="stat-value perfect">{{
                                            currentStats.home_team.stats.receive.perfect
                                            || '0' }}
                                        </div>
                                        <div class="stat-value percent"
                                            v-if="currentStats.home_team.stats.receive.total > 0">
                                            ({{ formatPercent(((currentStats.home_team.stats.receive.perfect /
                                                currentStats.home_team.stats.receive.total) * 100)) }})
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'home', 'positive')"
                                            :class="getSortClass('receive', 'home', 'positive')"
                                            title="Позитивный прием">+
                                        </div>
                                        <div class="stat-value positive">{{
                                            currentStats.home_team.stats.receive.positive
                                            || '0' }}
                                        </div>
                                        <div class="stat-value percent"
                                            v-if="currentStats.home_team.stats.receive.total > 0">
                                            ({{ formatPercent(((((currentStats.home_team.stats.receive.perfect || 0) +
                                                (currentStats.home_team.stats.receive.positive || 0)) /
                                                currentStats.home_team.stats.receive.total) * 100)) }})
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'home', 'neutral')"
                                            :class="getSortClass('receive', 'home', 'neutral')" title="Прием на 'знак'">
                                            !</div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.receive.neutral
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'home', 'negative')"
                                            :class="getSortClass('receive', 'home', 'negative')"
                                            title="Прием на 'минус'">
                                            -
                                        </div>
                                        <div class="stat-value neutral">{{
                                            currentStats.home_team.stats.receive.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'home', 'slash')"
                                            :class="getSortClass('receive', 'home', 'slash')"
                                            title="Прием на ту сторону">/
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.home_team.stats.receive.slash
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'home', 'error')"
                                            :class="getSortClass('receive', 'home', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.receive.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'away', 'total')"
                                            :class="getSortClass('receive', 'away', 'total')"
                                            title="Общее количество приемов">Общее</div>
                                        <div class="stat-value total">{{ currentStats.away_team.stats.receive.total }}
                                        </div>
                                        <div class="stat-value efficiency" title="Эффективность приема">({{
                                            formatPercent(currentStats.away_team.stats.receive.efficiency) }})</div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'away', 'perfect')"
                                            :class="getSortClass('receive', 'away', 'perfect')" title="Идеальный прием">
                                            #</div>
                                        <div class="stat-value perfect">{{
                                            currentStats.away_team.stats.receive.perfect
                                            || '0' }}
                                        </div>
                                        <div class="stat-value percent"
                                            v-if="currentStats.away_team.stats.receive.total > 0">
                                            ({{ formatPercent(((currentStats.away_team.stats.receive.perfect /
                                                currentStats.away_team.stats.receive.total) * 100)) }})
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'away', 'positive')"
                                            :class="getSortClass('receive', 'away', 'positive')"
                                            title="Позитивный прием">+
                                        </div>
                                        <div class="stat-value positive">{{
                                            currentStats.away_team.stats.receive.positive
                                            || '0' }}
                                        </div>
                                        <div class="stat-value percent"
                                            v-if="currentStats.away_team.stats.receive.total > 0">
                                            ({{ formatPercent(((((currentStats.away_team.stats.receive.perfect || 0) +
                                                (currentStats.away_team.stats.receive.positive || 0)) /
                                                currentStats.away_team.stats.receive.total) * 100)) }})
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'away', 'neutral')"
                                            :class="getSortClass('receive', 'away', 'neutral')" title="Прием на 'знак'">
                                            !</div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.receive.neutral
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'away', 'negative')"
                                            :class="getSortClass('receive', 'away', 'negative')"
                                            title="Прием на 'минус'">
                                            -
                                        </div>
                                        <div class="stat-value neutral">{{
                                            currentStats.away_team.stats.receive.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'away', 'slash')"
                                            :class="getSortClass('receive', 'away', 'slash')"
                                            title="Прием на ту сторону">/
                                        </div>
                                        <div class="stat-value neutral">{{ currentStats.away_team.stats.receive.slash
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('receive', 'away', 'error')"
                                            :class="getSortClass('receive', 'away', 'error')" title="Ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.receive.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция приема -->
                    <div v-if="expandedSections.receive" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('receive', 'home')"
                                    :key="`receive-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <div class="stat-col-group">
                                            <span class="stat-col total">{{ player.stats.total }}</span>
                                            <span class="stat-col efficiency" title="Эффективность приема">({{
                                                formatPercent(player.stats.efficiency) }})</span>
                                        </div>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                            <span class="stat-col percent" v-if="player.stats.total > 0">({{
                                                formatPercent(((player.stats.perfect || 0) / player.stats.total * 100))
                                                }})</span>
                                        </div>
                                        <div class="stat-col-group">
                                            <span class="stat-col positive">{{ player.stats.positive || '0' }}</span>
                                            <span class="stat-col percent" v-if="player.stats.total > 0">({{
                                                formatPercent((((player.stats.perfect || 0) + (player.stats.positive ||
                                                    0)) / player.stats.total * 100)) }})</span>
                                        </div>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('receive', 'away')"
                                    :key="`receive-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <div class="stat-col-group">
                                            <span class="stat-col total">{{ player.stats.total }}</span>
                                            <span class="stat-col efficiency" title="Эффективность приема">({{
                                                formatPercent(player.stats.efficiency) }})</span>
                                        </div>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.perfect || '0' }}</span>
                                            <span class="stat-col percent" v-if="player.stats.total > 0">({{
                                                formatPercent(((player.stats.perfect || 0) / player.stats.total * 100))
                                                }})</span>
                                        </div>
                                        <div class="stat-col-group">
                                            <span class="stat-col positive">{{ player.stats.positive || '0' }}</span>
                                            <span class="stat-col percent" v-if="player.stats.total > 0">({{
                                                formatPercent((((player.stats.perfect || 0) + (player.stats.positive ||
                                                0)) / player.stats.total * 100)) }})</span>
                                        </div>
                                        <span class="stat-col neutral">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col neutral">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Защита -->
                    <div class="stat-section">
                        <div class="stat-header" @click="toggleExpanded('dig')">
                            <span class="expand-icon" :class="{ expanded: expandedSections.dig }">▶</span>
                            <span class="stat-title">Защита</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'home', 'total')"
                                            :class="getSortClass('dig', 'home', 'total')" title="Общее количество">Общее
                                        </div>
                                        <div class="stat-value total">{{ currentStats.home_team.stats.dig.total }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'home', 'perfect')"
                                            :class="getSortClass('dig', 'home', 'perfect')" title="Качество идеально">#
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.dig.perfect
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'home', 'positive')"
                                            :class="getSortClass('dig', 'home', 'positive')" title="Качество хорошо">+
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.dig.positive
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'home', 'neutral')"
                                            :class="getSortClass('dig', 'home', 'neutral')" title="Качество нормально">!
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.dig.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'home', 'slash')"
                                            :class="getSortClass('dig', 'home', 'slash')" title="На ту сторону">/</div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.dig.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'home', 'negative')"
                                            :class="getSortClass('dig', 'home', 'negative')" title="Качество плохо">-
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.dig.negative ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'home', 'error')"
                                            :class="getSortClass('dig', 'home', 'error')" title="Качество ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.dig.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'away', 'total')"
                                            :class="getSortClass('dig', 'away', 'total')" title="Общее количество">Общее
                                        </div>
                                        <div class="stat-value total">{{ currentStats.away_team.stats.dig.total }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'away', 'perfect')"
                                            :class="getSortClass('dig', 'away', 'perfect')" title="Качество идеально">#
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.dig.perfect
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'away', 'positive')"
                                            :class="getSortClass('dig', 'away', 'positive')" title="Качество хорошо">+
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.dig.positive
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'away', 'neutral')"
                                            :class="getSortClass('dig', 'away', 'neutral')" title="Качество нормально">!
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.dig.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'away', 'slash')"
                                            :class="getSortClass('dig', 'away', 'slash')" title="На ту сторону">/</div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.dig.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'away', 'negative')"
                                            :class="getSortClass('dig', 'away', 'negative')" title="Качество плохо">-
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.dig.negative ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('dig', 'away', 'error')"
                                            :class="getSortClass('dig', 'away', 'error')" title="Качество ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.dig.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция защиты -->
                    <div v-if="expandedSections.dig" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('dig', 'home')"
                                    :key="`dig-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total }}</span>
                                        <span class="stat-col">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('dig', 'away')"
                                    :key="`dig-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total }}</span>
                                        <span class="stat-col">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Передача -->
                    <div class="stat-section">
                        <div class="stat-header" @click="toggleExpanded('set')">
                            <span class="expand-icon" :class="{ expanded: expandedSections.set }">▶</span>
                            <span class="stat-title">Передача</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'home', 'total')"
                                            :class="getSortClass('set', 'home', 'total')" title="Общее количество">Общее
                                        </div>
                                        <div class="stat-value total">{{ currentStats.home_team.stats.set.total }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'home', 'perfect')"
                                            :class="getSortClass('set', 'home', 'perfect')" title="Качество идеально">#
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.set.perfect
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'home', 'positive')"
                                            :class="getSortClass('set', 'home', 'positive')" title="Качество хорошо">+
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.set.positive
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'home', 'neutral')"
                                            :class="getSortClass('set', 'home', 'neutral')" title="Качество нормально">!
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.set.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'home', 'slash')"
                                            :class="getSortClass('set', 'home', 'slash')" title="На ту сторону">/</div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.set.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'home', 'negative')"
                                            :class="getSortClass('set', 'home', 'negative')" title="Качество плохо">-
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.set.negative ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'home', 'error')"
                                            :class="getSortClass('set', 'home', 'error')" title="Качество ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.set.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'away', 'total')"
                                            :class="getSortClass('set', 'away', 'total')" title="Общее количество">Общее
                                        </div>
                                        <div class="stat-value total">{{ currentStats.away_team.stats.set.total }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'away', 'perfect')"
                                            :class="getSortClass('set', 'away', 'perfect')" title="Качество идеально">#
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.set.perfect
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'away', 'positive')"
                                            :class="getSortClass('set', 'away', 'positive')" title="Качество хорошо">+
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.set.positive
                                            || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'away', 'neutral')"
                                            :class="getSortClass('set', 'away', 'neutral')" title="Качество нормально">!
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.set.neutral ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'away', 'slash')"
                                            :class="getSortClass('set', 'away', 'slash')" title="На ту сторону">/</div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.set.slash ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'away', 'negative')"
                                            :class="getSortClass('set', 'away', 'negative')" title="Качество плохо">-
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.set.negative ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label" @click="handleStatLabelClick('set', 'away', 'error')"
                                            :class="getSortClass('set', 'away', 'error')" title="Качество ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.set.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция передачи -->
                    <div v-if="expandedSections.set" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('set', 'home')"
                                    :key="`set-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total }}</span>
                                        <span class="stat-col">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('set', 'away')"
                                    :key="`set-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total }}</span>
                                        <span class="stat-col">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Свободный мяч -->
                    <div class="stat-section">
                        <div class="stat-header" @click="toggleExpanded('freeball')">
                            <span class="expand-icon" :class="{ expanded: expandedSections.freeball }">▶</span>
                            <span class="stat-title">Freeball</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'home', 'total')"
                                            :class="getSortClass('freeball', 'home', 'total')" title="Общее количество">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.home_team.stats.freeball.total }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'home', 'perfect')"
                                            :class="getSortClass('freeball', 'home', 'perfect')"
                                            title="Качество идеально">#
                                        </div>
                                        <div class="stat-value">{{
                                            currentStats.home_team.stats.freeball.perfect || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'home', 'positive')"
                                            :class="getSortClass('freeball', 'home', 'positive')"
                                            title="Качество хорошо">+
                                        </div>
                                        <div class="stat-value">{{
                                            currentStats.home_team.stats.freeball.positive || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'home', 'neutral')"
                                            :class="getSortClass('freeball', 'home', 'neutral')"
                                            title="Качество нормально">
                                            !</div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.freeball.neutral
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'home', 'slash')"
                                            :class="getSortClass('freeball', 'home', 'slash')" title="На ту сторону">/
                                        </div>
                                        <div class="stat-value">{{ currentStats.home_team.stats.freeball.slash
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'home', 'negative')"
                                            :class="getSortClass('freeball', 'home', 'negative')"
                                            title="Качество плохо">-
                                        </div>
                                        <div class="stat-value">{{
                                            currentStats.home_team.stats.freeball.negative
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'home', 'error')"
                                            :class="getSortClass('freeball', 'home', 'error')" title="Качество ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.home_team.stats.freeball.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'away', 'total')"
                                            :class="getSortClass('freeball', 'away', 'total')" title="Общее количество">
                                            Общее</div>
                                        <div class="stat-value total">{{ currentStats.away_team.stats.freeball.total }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'away', 'perfect')"
                                            :class="getSortClass('freeball', 'away', 'perfect')"
                                            title="Качество идеально">#
                                        </div>
                                        <div class="stat-value">{{
                                            currentStats.away_team.stats.freeball.perfect || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'away', 'positive')"
                                            :class="getSortClass('freeball', 'away', 'positive')"
                                            title="Качество хорошо">+
                                        </div>
                                        <div class="stat-value">{{
                                            currentStats.away_team.stats.freeball.positive || '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'away', 'neutral')"
                                            :class="getSortClass('freeball', 'away', 'neutral')"
                                            title="Качество нормально">
                                            !</div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.freeball.neutral
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'away', 'slash')"
                                            :class="getSortClass('freeball', 'away', 'slash')" title="На ту сторону">/
                                        </div>
                                        <div class="stat-value">{{ currentStats.away_team.stats.freeball.slash
                                            ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'away', 'negative')"
                                            :class="getSortClass('freeball', 'away', 'negative')"
                                            title="Качество плохо">-
                                        </div>
                                        <div class="stat-value">{{
                                            currentStats.away_team.stats.freeball.negative ||
                                            '0' }}
                                        </div>
                                    </div>
                                    <div class="stat-value-item">
                                        <div class="stat-label"
                                            @click="handleStatLabelClick('freeball', 'away', 'error')"
                                            :class="getSortClass('freeball', 'away', 'error')" title="Качество ошибка">=
                                        </div>
                                        <div class="stat-value error">{{ currentStats.away_team.stats.freeball.error ||
                                            '0' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция freeball -->
                    <div v-if="expandedSections.freeball" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('freeball', 'home')"
                                    :key="`freeball-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total }}</span>
                                        <span class="stat-col">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('freeball', 'away')"
                                    :key="`freeball-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.total }}</span>
                                        <span class="stat-col">{{ player.stats.perfect || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.positive || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.neutral || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.slash || '0' }}</span>
                                        <span class="stat-col">{{ player.stats.negative || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.error > 0 ?
                                            player.stats.error : '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Атака с приема -->
                    <div class="stat-section attack-from-receive">
                        <div class="stat-header" @click="toggleExpanded('attack_from_receive')">
                            <span class="expand-icon"
                                :class="{ expanded: expandedSections.attack_from_receive }">▶</span>
                            <span class="stat-title">Атака с приёма</span>
                        </div>
                        <div class="stat-row">
                            <div class="stat-cell home-cell">
                                <div class="stat-values-row">
                                    <!-- Позитивный приём для хозяев -->
                                    <div class="stat-value-group">
                                        <div class="stat-value-item">
                                            <div class="stat-label">#+</div>
                                            <div class="stat-value total">{{
                                                (currentStats.home_team.attack_from_receive?.positive_receive?.total) ||
                                                0 }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">#</div>
                                            <div class="stat-value perfect">{{
                                                currentStats.home_team.attack_from_receive?.positive_receive?.perfect ||
                                                0 }}</div>
                                            <div class="stat-value percent"
                                                v-if="currentStats.home_team.attack_from_receive?.positive_receive?.total > 0">
                                                ({{
                                                    currentStats.home_team.attack_from_receive.positive_receive.success_rate
                                                    || 0 }}%)</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">/</div>
                                            <div class="stat-value">{{
                                                currentStats.home_team.attack_from_receive?.positive_receive?.slash || 0
                                            }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">=</div>
                                            <div class="stat-value error">{{
                                                currentStats.home_team.attack_from_receive?.positive_receive?.error || 0
                                            }}</div>
                                        </div>
                                    </div>

                                    <!-- Негативный приём для хозяев -->
                                    <div class="stat-value-group">
                                        <div class="stat-value-item">
                                            <div class="stat-label">!-</div>
                                            <div class="stat-value total">{{
                                                (currentStats.home_team.attack_from_receive?.negative_receive?.total) ||
                                                0 }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">#</div>
                                            <div class="stat-value perfect">{{
                                                currentStats.home_team.attack_from_receive?.negative_receive?.perfect ||
                                                0 }}</div>
                                            <div class="stat-value percent"
                                                v-if="currentStats.home_team.attack_from_receive?.negative_receive?.total > 0">
                                                ({{
                                                    currentStats.home_team.attack_from_receive.negative_receive.success_rate
                                                    || 0 }}%)</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">/</div>
                                            <div class="stat-value">{{
                                                currentStats.home_team.attack_from_receive?.negative_receive?.slash || 0
                                            }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">=</div>
                                            <div class="stat-value error">{{
                                                currentStats.home_team.attack_from_receive?.negative_receive?.error || 0
                                            }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="stat-cell away-cell">
                                <div class="stat-values-row">
                                    <!-- Позитивный приём для гостей -->
                                    <div class="stat-value-group">
                                        <div class="stat-value-item">
                                            <div class="stat-label">#+</div>
                                            <div class="stat-value total">{{
                                                (currentStats.away_team.attack_from_receive?.positive_receive?.total) ||
                                                0 }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">#</div>
                                            <div class="stat-value perfect">{{
                                                currentStats.away_team.attack_from_receive?.positive_receive?.perfect ||
                                                0 }}</div>
                                            <div class="stat-value percent"
                                                v-if="currentStats.away_team.attack_from_receive?.positive_receive?.total > 0">
                                                ({{
                                                    currentStats.away_team.attack_from_receive.positive_receive.success_rate
                                                    || 0 }}%)</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">/</div>
                                            <div class="stat-value">{{
                                                currentStats.away_team.attack_from_receive?.positive_receive?.slash || 0
                                            }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">=</div>
                                            <div class="stat-value error">{{
                                                currentStats.away_team.attack_from_receive?.positive_receive?.error || 0
                                            }}</div>
                                        </div>
                                    </div>

                                    <!-- Негативный приём для гостей -->
                                    <div class="stat-value-group">
                                        <div class="stat-value-item">
                                            <div class="stat-label">!-</div>
                                            <div class="stat-value total">{{
                                                (currentStats.away_team.attack_from_receive?.negative_receive?.total) ||
                                                0 }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">#</div>
                                            <div class="stat-value perfect">{{
                                                currentStats.away_team.attack_from_receive?.negative_receive?.perfect ||
                                                0 }}</div>
                                            <div class="stat-value percent"
                                                v-if="currentStats.away_team.attack_from_receive?.negative_receive?.total > 0">
                                                ({{
                                                    currentStats.away_team.attack_from_receive.negative_receive.success_rate
                                                    || 0 }}%)</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">/</div>
                                            <div class="stat-value">{{
                                                currentStats.away_team.attack_from_receive?.negative_receive?.slash || 0
                                            }}</div>
                                        </div>
                                        <div class="stat-value-item">
                                            <div class="stat-label">=</div>
                                            <div class="stat-value error">{{
                                                currentStats.away_team.attack_from_receive?.negative_receive?.error || 0
                                            }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Раскрывающаяся секция: атака с приёма -->
                    <div v-if="expandedSections.attack_from_receive" class="expanded-section">
                        <div class="players-breakdown">
                            <div class="team-players home-players">
                                <div v-for="player in getSortedPlayersByStat('attack_from_receive', 'home')"
                                    :key="`attack_from_receive-home-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.pos_total || '0' }}</span>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.pos_perfect || '0' }}</span>
                                            <span class="stat-col percent">({{ player.stats.pos_percent || '0'
                                            }}%)</span>
                                        </div>
                                        <span class="stat-col error">{{ player.stats.pos_blocked || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.pos_error || '0' }}</span>
                                        <span class="stat-col total">{{ player.stats.neg_total || '0' }}</span>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.neg_perfect || '0' }}</span>
                                            <span class="stat-col percent">({{ player.stats.neg_percent || '0'
                                            }}%)</span>
                                        </div>
                                        <span class="stat-col error">{{ player.stats.neg_blocked || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.neg_error || '0' }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="team-players away-players">
                                <div v-for="player in getSortedPlayersByStat('attack_from_receive', 'away')"
                                    :key="`attack_from_receive-away-${player.id}`" class="player-stat-item">
                                    <div class="player-info">
                                        <span class="player-number">{{ player.number }}</span>
                                        <img v-if="player.photo_url" :src="player.photo_url" :alt="playerDisplayName(player)"
                                            class="player-photo" />
                                        <div v-else class="player-photo-placeholder">{{ playerNameInitial(player) }}</div>
                                        <component
                                            :is="player.url ? 'a' : 'span'"
                                            :href="player.url || undefined"
                                            class="player-name"
                                            :class="{ 'player-name-link': !!player.url }"
                                        >
                                            <RegisteredBadge :status="player.status" size="sm" />
                                            {{ playerDisplayName(player) }}
                                        </component>
                                    </div>
                                    <div class="player-stats-grid">
                                        <span class="stat-col total">{{ player.stats.pos_total || '0' }}</span>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.pos_perfect || '0' }}</span>
                                            <span class="stat-col percent">({{ player.stats.pos_percent || '0'
                                            }}%)</span>
                                        </div>
                                        <span class="stat-col error">{{ player.stats.pos_blocked || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.pos_error || '0' }}</span>
                                        <span class="stat-col total">{{ player.stats.neg_total || '0' }}</span>
                                        <div class="stat-col-group">
                                            <span class="stat-col perfect">{{ player.stats.neg_perfect || '0' }}</span>
                                            <span class="stat-col percent">({{ player.stats.neg_percent || '0'
                                            }}%)</span>
                                        </div>
                                        <span class="stat-col error">{{ player.stats.neg_blocked || '0' }}</span>
                                        <span class="stat-col error">{{ player.stats.neg_error || '0' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </main-layout>
</template>

<script setup>
import { ref, computed } from 'vue';
import SeoHead from '@/Components/SeoHead.vue';
import SeoBreadcrumbs from '@/Components/SeoBreadcrumbs.vue';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import RegisteredBadge from '@/Components/shared/RegisteredBadge.vue';
import PointFlowChart from '@/components/game/PointFlowChart.vue';
import { teamPublicPath } from '@/utils/teamUrl';

const props = defineProps({
    seo: {
        type: Object,
        default: null,
    },
    game: {
        type: Object,
        required: true
    },
    statistics: {
        type: Object,
        required: true
    },
    pointFlow: {
        type: Object,
        default: null,
    },
});

// Define components for this page
defineOptions({
    components: {
        // No additional components needed
    }
});

// Reactive data
const selectedSet = ref('all'); // 'all' или номер сета
const expandedSections = ref({
    attack: false,
    serve: false,
    block: false,
    receive: false,
    dig: false,
    set: false,
    freeball: false,
    attack_from_receive: false
});
const sortField = ref(''); // поле для сортировки (например 'attack.total')
const sortDirection = ref('desc'); // 'asc' или 'desc'

// Computed properties
const setsRange = computed(() => {
    const count = props.statistics.sets_count;
    return Array.from({ length: count }, (_, i) => i + 1);
});

const currentStats = computed(() => {
    if (selectedSet.value === 'all' || !props.statistics.sets_stats) {
        return props.statistics;
    }

    // Try both number and string keys (PHP sends string keys, JS uses numbers)
    const setNumber = selectedSet.value.toString();
    const setStats = props.statistics.sets_stats[setNumber];

    if (!setStats) {
        console.warn('No stats found for set', setNumber, 'available sets:', Object.keys(props.statistics.sets_stats));
        return props.statistics;
    }

    return {
        home_team: {
            ...props.statistics.home_team,
            stats: setStats.home_team.stats,
            attack_players: setStats.home_team.attack_players,
            serve_players: setStats.home_team.serve_players,
            block_players: setStats.home_team.block_players,
            receive_players: setStats.home_team.receive_players,
            dig_players: setStats.home_team.dig_players,
            set_players: setStats.home_team.set_players,
            freeball_players: setStats.home_team.freeball_players,
            attack_from_receive: setStats.home_team.attack_from_receive,
            attack_from_receive_players: setStats.home_team.attack_from_receive_players,
        },
        away_team: {
            ...props.statistics.away_team,
            stats: setStats.away_team.stats,
            attack_players: setStats.away_team.attack_players,
            serve_players: setStats.away_team.serve_players,
            block_players: setStats.away_team.block_players,
            receive_players: setStats.away_team.receive_players,
            dig_players: setStats.away_team.dig_players,
            set_players: setStats.away_team.set_players,
            freeball_players: setStats.away_team.freeball_players,
            attack_from_receive: setStats.away_team.attack_from_receive,
            attack_from_receive_players: setStats.away_team.attack_from_receive_players,
        },
    };
});

const hasStatsData = computed(() => {
    const totalHomeActions = props.statistics.home_team.stats.attack.total +
        props.statistics.home_team.stats.serve.total +
        props.statistics.home_team.stats.receive.total +
        props.statistics.home_team.stats.block.total +
        props.statistics.home_team.stats.dig.total +
        props.statistics.home_team.stats.set.total +
        props.statistics.home_team.stats.freeball.total;

    const totalAwayActions = props.statistics.away_team.stats.attack.total +
        props.statistics.away_team.stats.serve.total +
        props.statistics.away_team.stats.receive.total +
        props.statistics.away_team.stats.block.total +
        props.statistics.away_team.stats.dig.total +
        props.statistics.away_team.stats.set.total +
        props.statistics.away_team.stats.freeball.total;

    return totalHomeActions > 0 || totalAwayActions > 0;
});

const hasPointFlow = computed(() => {
    const sets = props.pointFlow?.sets;
    return !!sets && Object.keys(sets).length > 0;
});

const hasMatchVideos = computed(
    () => !!(props.game.match_video_embed_url || props.game.match_analysis_embed_url)
);

const homeFlowColor = computed(() => props.game.home_team?.primary_color || '#275092');
const awayFlowColor = computed(() => props.game.away_team?.primary_color || '#06b6d4');

// Methods
const toggleExpanded = (section) => {
    expandedSections.value[section] = !expandedSections.value[section];
};

const handleStatLabelClick = (statType, team, field) => {
    const sortKey = `${statType}.${team}`;

    if (sortField.value === `${sortKey}.${field}`) {
        // То же поле - меняем направление
        sortDirection.value = sortDirection.value === 'desc' ? 'asc' : 'desc';
    } else {
        // Новое поле - устанавливаем сортировку по убыванию
        sortField.value = `${sortKey}.${field}`;
        sortDirection.value = 'desc';
    }
};

const getSortClass = (statType, team, field) => {
    const sortKey = `${statType}.${team}`;
    if (sortField.value === `${sortKey}.${field}`) {
        return sortDirection.value === 'desc' ? 'sorted-desc' : 'sorted-asc';
    }
    return '';
};

// Форматирование процентов: если целое — без дробной части, иначе 2 знака
const formatPercent = (v) => {
    const num = Number(v);
    if (!isFinite(num)) return '0%';
    return Number.isInteger(num) ? `${num}%` : `${num.toFixed(2)}%`;
};

const getSortedPlayersByStat = (statType, team) => {
    const teamData = team === 'home' ? currentStats.value.home_team : currentStats.value.away_team;
    const playersKey = `${statType}_players`;
    let players = teamData[playersKey] || [];

    // Если есть активная сортировка для этого типа статистики и команды
    const sortKey = `${statType}.${team}`;
    let shouldSort = sortField.value.startsWith(sortKey + '.');

    if (shouldSort) {
        const field = sortField.value.replace(sortKey + '.', '');
        players = [...players].sort((a, b) => {
            let aVal, bVal;

            if (field === 'positive_total' && (statType === 'set' || statType === 'freeball' || statType === 'dig')) {
                // Для передачи, freeball и защиты: позитивные = perfect + positive
                aVal = (a.stats.perfect || 0) + (a.stats.positive || 0);
                bVal = (b.stats.perfect || 0) + (b.stats.positive || 0);
            } else if (field === 'perfect' && statType === 'set') {
                // Для передачи: без атаки = perfect + errors
                aVal = (a.stats.perfect || 0) + (a.stats.errors || 0);
                bVal = (b.stats.perfect || 0) + (b.stats.errors || 0);
            } else {
                aVal = a.stats[field] || 0;
                bVal = b.stats[field] || 0;
            }

            if (sortDirection.value === 'asc') {
                return aVal - bVal;
            } else {
                return bVal - aVal;
            }
        });
    }

    return players;
};


// Методы
const playerDisplayName = (player) => player?.name || 'Игрок';
const playerNameInitial = (player) => {
    const name = playerDisplayName(player);
    return name.charAt(0) || '?';
};

const getCompetitionName = (competitionType) => {
    const baseNames = {
        'championship': 'чемпионат Республики Беларусь',
        'cup': 'Кубок Республики Беларусь',
        'supercup': 'СуперКубок Республики Беларусь'
    };

    const baseName = baseNames[competitionType] || competitionType;

    // Добавляем номер сезона для чемпионата
    if (competitionType === 'championship' && props.game.season?.id) {
        // Используем ID сезона как номер чемпионата
        return `${props.game.season.id}-й ${baseName}`;
    }

    return baseName.charAt(0).toUpperCase() + baseName.slice(1);
};

const getStageName = (stage) => {
    const names = {
        'regular-1': 'Регулярный сезон (1-й круг)',
        'regular-2': 'Регулярный сезон (2-й круг)',
        'quarterfinal': 'Четвертьфинал',
        'semifinal': 'Полуфинал',
        'final': 'Финал',
        'third_place': 'Матч за 3-е место',
        '5-places': 'Матч за 5-е место',
        'group_stage': 'Групповой этап',
        'dop': 'Дополнительный этап'
    };
    return names[stage] || stage;
};

const formatMatchTime = (timeString) => {
    if (!timeString) return '';

    try {
        // Если время в формате ISO (2026-01-19T17:00:00.000000Z)
        if (timeString.includes('T')) {
            // Извлекаем время без timezone conversion
            const timeMatch = timeString.match(/T(\d{2}:\d{2})/);
            if (timeMatch) {
                return timeMatch[1];
            }
        }

        // Если уже в правильном формате (17:00)
        if (timeString.match(/^\d{1,2}:\d{2}$/)) {
            return timeString;
        }

        // Попытка распарсить как время
        const date = new Date(`1970-01-01T${timeString}`);
        if (!isNaN(date.getTime())) {
            return date.toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'UTC' // Используем UTC чтобы избежать timezone conversion
            });
        }
    } catch (e) {
        // В случае ошибки возвращаем оригинальную строку
    }

    return timeString;
};

const formatSetsScore = (setsScore) => {
    if (!setsScore || !Array.isArray(setsScore)) return '';

    return setsScore.map(set => {
        if (typeof set === 'object' && set.home !== undefined && set.away !== undefined) {
            return `${set.home}:${set.away}`;
        }
        return set;
    }).join(' • ');
};
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.no-stats-message {
    background: $color-bg-primary;
    border-radius: 12px;
    padding: 3rem 2rem;
    margin-bottom: 2rem;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .no-stats-icon {
        font-size: 4rem;
        margin-bottom: 1rem;
        opacity: 0.5;
    }

    h3 {
        font-size: 1.25rem;
        font-weight: 600;
        color: $color-text-primary;
        margin-bottom: 0.5rem;
    }

    p {
        color: $color-text-muted;
        font-size: 0.875rem;
    }
}

.no-players {
    background: $color-bg-secondary;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;

    p {
        color: $color-text-muted;
        font-size: 0.875rem;
        margin: 0;
    }
}

.match-header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem 2rem;
    align-items: center;
    background: $color-bg-primary;
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &--with-video {
        grid-template-columns: 1fr 1fr 1fr;
    }
}

.match-header__score {
    min-width: 0;
}

.match-header__info {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
}

.match-header__videos {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
}

.teams-score-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 0.5rem;

    .team {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        min-width: 0;

        &--link {
            color: inherit;
            text-decoration: none;

            &:hover .team-name {
                text-decoration: underline;
            }
        }

        .team-logo {
            width: 64px;
            height: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            font-weight: bold;
            background: none;

            img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
            }

            &.placeholder {
                background: $color-bg-secondary;
                border: 2px solid $color-border-lighter;
            }
        }

        .team-name {
            font-size: 0.95rem;
            font-weight: 600;
            color: $color-text-primary;
            text-align: center;
            overflow-wrap: anywhere;
        }
    }

    .match-score {
        flex: 0 0 auto;
        margin: 0 0.5rem;

        .score-main {
            font-size: 2.25rem;
            font-weight: 700;
            color: $color-text-primary;
            text-align: center;
            white-space: nowrap;
        }
    }
}

.sets-score-centered {
    text-align: center;

    .sets-label {
        font-size: 0.75rem;
        color: $color-text-muted;
        margin-bottom: 0.25rem;
    }

    .sets-results {
        font-size: 0.85rem;
        font-weight: 500;
        color: $color-text-primary;
    }
}

.match-info {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .competition-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: $color-text-primary;
    }

    .stage-info {
        font-size: 0.95rem;
        color: $color-text-result-secondary;
    }

    .match-date {
        font-size: 0.9rem;
        color: $color-text-primary;
        font-weight: 500;
    }

    .match-time {
        font-size: 0.9rem;
        color: $color-text-muted;
    }

    .match-venue {
        font-size: 0.85rem;
        color: $color-text-muted;
    }

    .match-referees {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        margin-top: 0.25rem;
        font-size: 0.8rem;
        color: $color-text-muted;
    }
}

.match-video {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: $radius-md;
    overflow: hidden;
    background: $color-bg-secondary;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    iframe {
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
    }
}

.controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    gap: 1rem;
    position: sticky;
    top: 60px;
    /* Учитываем высоту header (60px) + margin-top (20px) + небольшой отступ */
    z-index: 50;

    .set-tabs {
        display: flex;
        gap: 0.25rem;
        background: #f1f5f9;
        padding: 0.25rem;
        border-radius: 8px;

        .set-tab-btn {
            padding: 0.75rem 1.25rem;
            background: transparent;
            border: none;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 500;
            color: #275092;
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
                color: #475569;
            }

            &.active {
                background: #fff;
                color: #275092;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            }
        }
    }

}

.statistics-content {
    background: $color-bg-primary;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stats-table {

    .teams-headers {
        display: flex;
        border-bottom: 1px solid $color-border-lighter;
        background: $color-bg-secondary;

        .team-header-cell {
            padding: 0.75rem 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 1;

            .team-logo-header {
                width: 50px;
                height: 50px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1.5rem;
                font-weight: bold;
                color: $color-text-primary;

                img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }

                &.placeholder {
                    background: $color-bg-primary;
                    border: 2px solid $color-border-lighter;
                    border-radius: 8px;
                }
            }
        }
    }

    .stats-rows {
        .stat-section {
            border-bottom: 1px solid $color-border-lighter;

            .stat-header {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;
                padding: 0.75rem 1rem;
                background: $color-bg-secondary;
                cursor: pointer;
                border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                transition: background-color 0.15s ease;

                &:hover {
                    background: rgba(39, 80, 146, 0.05);
                }

                .expand-icon {
                    font-size: 0.8rem;
                    transition: transform 0.2s ease;
                    color: $color-text-muted;
                    cursor: pointer;
                    user-select: none;
                    padding: 2px;
                    border-radius: 2px;

                    &:hover {
                        color: $color-text-primary;
                        background: rgba(0, 0, 0, 0.05);
                    }

                    &.expanded {
                        transform: rotate(90deg);
                        color: $color-text-primary;
                    }
                }

                .stat-title {
                    font-weight: 600;
                    color: $color-text-primary;
                    font-size: 0.9rem;
                }
            }

            .stat-row {
                display: flex;

                .stat-cell {
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    flex: 1;

                    .stat-values-row {
                        display: flex;
                        gap: 0.5rem;
                        align-items: flex-start;

                        .stat-value-group {
                            display: flex;
                            gap: 0.5rem;
                            align-items: flex-start;
                        }

                        .stat-value-item {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            gap: 0.25rem;
                            width: 40px;

                            .stat-label {
                                font-size: 0.75rem;
                                font-weight: 500;
                                color: $color-text-muted;
                                text-align: center;
                                cursor: pointer;
                                user-select: none;
                                transition: all 0.15s ease;

                                &:hover {
                                    color: $color-text-primary;
                                }

                                &.sorted-asc,
                                &.sorted-desc {
                                    color: $color-text-primary;
                                }
                            }

                            .stat-value {
                                font-size: 1rem;
                                font-weight: 700;
                                text-align: center;

                                &.total {
                                    color: $color-text-primary;
                                }

                                &.efficiency {
                                    color: #2196f3;
                                    font-size: 10px;
                                    font-weight: 600;
                                }

                                &.perfect,
                                &.positive,
                                &.percent {
                                    color: #10b981;
                                }

                                &.percent {
                                    font-size: 10px;
                                }

                                // ensure percent sizing in attack-from-receive block
                                .attack-from-receive &.percent {
                                    font-size: 10px;
                                }

                                &.neutral {
                                    color: #6b7280;
                                }

                                &.slash,
                                &.negative {
                                    color: #f59e0b;
                                }

                                &.error {
                                    color: #ef4444;
                                }
                            }
                        }
                    }
                }
            }
        }

        .expanded-section {
            background: rgba(39, 80, 146, 0.02);
            border-bottom: 1px solid $color-border-lighter;

            .players-breakdown {
                display: flex;
                gap: 0;

                .team-players {
                    border-left: 1px solid $color-border-lighter;
                    border-right: 1px solid $color-border-lighter;
                    flex: 1;

                    &:first-child {
                        border-left: none;
                    }

                    &:last-child {
                        border-right: none;
                    }

                    .player-stat-item {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 0.75rem 1rem;
                        border-bottom: 1px solid rgba(0, 0, 0, 0.05);

                        &:last-child {
                            border-bottom: none;
                        }

                        .player-info {
                            display: flex;
                            align-items: center;
                            gap: 0.5rem;

                            .player-number {
                                font-size: 0.9rem;
                                font-weight: 700;
                                color: $color-text-primary;
                                min-width: 24px;
                                text-align: center;
                            }

                            .player-photo {
                                width: 32px;
                                height: 32px;
                                border-radius: 8px;
                                @include person-photo-cover;
                            }

                            .player-photo-placeholder {
                                width: 32px;
                                height: 32px;
                                border-radius: 8px;
                                background: $color-bg-secondary;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                font-size: 0.8rem;
                                font-weight: 600;
                                color: $color-text-primary;
                            }

                            .player-name {
                                display: inline-flex;
                                align-items: center;
                                gap: 0.3rem;
                                font-size: 0.8rem;
                                font-weight: 500;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                max-width: 140px;
                            }
                        }

                        .player-stats-grid {
                            display: flex;
                            gap: 0.5rem;
                            align-items: center;
                            justify-content: center;

                            .stat-col-group {
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                gap: 2px;

                                .stat-col {
                                    width: 40px;
                                }
                            }

                            .stat-col {
                                text-align: center;
                                width: 40px;
                                font-size: 0.8rem;
                                font-weight: 600;

                                &.total {
                                    color: $color-text-primary;
                                }

                                &.efficiency {
                                    color: #2196f3;
                                    font-size: 10px;
                                    font-weight: 600;
                                }

                                &.perfect,
                                &.positive,
                                &.percent {
                                    color: #10b981;
                                }

                                &.percent {
                                    font-size: 10px;
                                }

                                &.neutral {
                                    color: #6b7280;
                                }

                                &.slash,
                                &.negative {
                                    color: #f59e0b;
                                }

                                &.error {
                                    color: #ef4444;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@include down($bp-md) {
    .match-header {
        grid-template-columns: 1fr;
        gap: $space-4;
        padding: $space-4;
        margin-bottom: $space-4;

        &--with-video {
            grid-template-columns: 1fr;
        }
    }

    .teams-score-row {
        width: 100%;
        gap: $space-3;
        flex-direction: column;
        align-items: stretch;

        .team {
            gap: $space-2;
            min-width: 0;
            flex-direction: row;
            justify-content: center;

            .team-logo {
                width: 56px;
                height: 56px;
                font-size: $font-size-xl;
            }

            .team-name {
                font-size: $font-size-sm;
                overflow-wrap: anywhere;
            }
        }

        .away-team {
            order: 2;
        }

        .match-score {
            align-self: center;
            margin: 0;
            order: 1;

            .score-main {
                font-size: $font-size-2xl;
                white-space: nowrap;
            }
        }
    }

    .sets-score-centered {
        .sets-label,
        .sets-results {
            font-size: $font-size-xs;
        }
    }

    .match-header__videos {
        max-width: 480px;
        width: 100%;
        margin-inline: auto;
    }

    .controls {
        top: $header-height;
        margin-bottom: $space-4;

        .set-tabs {
            max-width: 100%;
            overflow-x: auto;

            .set-tab-btn {
                flex: 0 0 auto;
                min-height: $touch-min;
                padding: $space-2 $space-3;
            }
        }
    }

    .statistics-content {
        overflow-x: auto;
        border-radius: $radius-md;
    }

    .stats-table {
        min-width: 640px;

        .teams-headers .team-header-cell {
            padding: $space-2 $space-3;
        }

        .stats-rows .stat-section {
            .stat-header {
                padding: $space-2 $space-3;
            }

            .stat-row .stat-cell {
                padding: $space-3;
            }
        }
    }
}
</style>
